import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import OSModal from "./OSModal";
import { Loader } from "./components/Loader";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from "./contexts/AuthContext";
import { ENDPOINTS } from "./endpoints";
import { temPermissao } from "./utils/permissoes";

// Tipos para prioridade e status
const prioridades = {
  AGUARDANDO: "bg-gray-200 text-gray-900",
  EMERGENCIAL: "bg-red-600 text-white",
  ALTA: "bg-orange-400 text-white",
  MEDIA: "bg-teal-400 text-white",
  BAIXA: "bg-green-700 text-white",
} as const;

type Prioridade = keyof typeof prioridades;

const statusCores = {
  AGUARDANDO: "bg-gray-200 text-gray-900",
  DISTRIBUIDA: "bg-gray-700 text-yellow-300",
  ACEITA: "bg-gray-700 text-cyan-300",
  CONCLUIDA: "bg-gray-700 text-green-400",
  CANCELADA: "bg-red-600 text-white",
} as const;

type Status = keyof typeof statusCores;

type OSApi = {
  id: number;
  dth_abertura: string;
  dth_fim: string;
  departamento_id: number;
  departamento_descricao: string;
  local_descricao: string;
  solicitante_nome: string;
  status_id: number;
  status_descricao: string;
  executor_id: number;
  executor_nome: string | null;
  prioridade_id: number;
  prioridade_descricao: string;
  descricao_os: string;
  obs_adm: string;
  descricao_servico_executar: string;
  materiais_necessarios: string;
  equipamentos_necessarios: string;
  profissionais_envolvidos: string;
  observacoes: string;
};

type OS = {
  id: number;
  inicio: string;
  fim: string;
  departamento: string;
  local: string;
  solicitante: string;
  status: Status;
  executor: string;
  prioridade: Prioridade;
  descricao: string;
  obs_adm: string;
  descricao_servico_executar: string;
  materiais_necessarios: string;
  equipamentos_necessarios: string;
  profissionais_envolvidos: string;
  observacoes: string;
};

function mapApiToOS(api: OSApi): OS & { departamento_id: number } {
  // Normaliza os valores para os enums usados na grid
  const prioridade = (api.prioridade_descricao?.toUpperCase() ?? "AGUARDANDO") as Prioridade;
  const status = (api.status_descricao?.toUpperCase() ?? "AGUARDANDO") as Status;
  return {
    id: api.id,
    inicio: api.dth_abertura,
    fim: api.dth_fim,
    departamento: api.departamento_descricao,
    departamento_id: api.departamento_id, // Preservar o departamento_id da API
    local: api.local_descricao,
    solicitante: api.solicitante_nome,
    status,
    executor: api.executor_nome || "-",
    prioridade,
    descricao: api.descricao_os,
    obs_adm: api.obs_adm,
    descricao_servico_executar: api.descricao_servico_executar,
    materiais_necessarios: api.materiais_necessarios,
    equipamentos_necessarios: api.equipamentos_necessarios,
    profissionais_envolvidos: api.profissionais_envolvidos,
    observacoes: api.observacoes,
  };
}

type ColunaOrdenavel = keyof Pick<OS, 'id' | 'inicio' | 'fim' | 'departamento' | 'local' | 'solicitante' | 'status' | 'executor' | 'prioridade'>;

type Direcao = 'asc' | 'desc';

// Função auxiliar para renderizar input de filtro com botão de limpar
function FiltroInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative flex items-center">
      <input
        className="w-full rounded px-1 py-1 text-xs bg-[#fff9c4] text-black pr-6"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          className="absolute right-1 text-gray-400 hover:text-red-500 text-xs px-1 focus:outline-none cursor-pointer"
          style={{ lineHeight: 1 }}
          onClick={() => onChange("")}
          tabIndex={-1}
          title="Limpar filtro"
        >
          ×
        </button>
      )}
    </div>
  );
}

// Função auxiliar para normalizar strings (remover acentos e espaços extras)
function normalizar(str: string) {
  return (str || "")
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

interface Filtros {
  id: string;
  inicio: string;
  fim: string;
  departamento: string;
  local: string;
  solicitante: string;
  status: string;
  executor: string;
  prioridade: string;
  contadores?: {
    aguardando: number;
    distribuidas: number;
    aceita: number;
    concluidas: number;
    canceladas: number;
  };
}

const OSGrade = forwardRef(function OSGrade({ unidade = "iacs", filtroStatus, filtroDepartamentoInicial, onLimparFiltroStatus, onFiltrosChange }: { unidade?: string, filtroStatus?: string, filtroDepartamentoInicial?: string, onLimparFiltroStatus?: () => void, onFiltrosChange?: (filtros: Filtros) => void }, ref) {
  const { loginGrupoId, loginLoginId, loginLoginNome, loginDepartamentoId, loginGrupoNome, permissoes, setLoginDepartamentoId } = useAuth();
  const [filtros, setFiltros] = useState<Filtros>({
    id: "",
    inicio: "",
    fim: "",
    departamento: filtroDepartamentoInicial || "",
    local: "",
    solicitante: "",
    status: filtroStatus || "",
    executor: "",
    prioridade: "",
  });
  const [sort, setSort] = useState<{ coluna: ColunaOrdenavel; direcao: Direcao }>({ coluna: 'id', direcao: 'desc' });
  const [expandida, setExpandida] = useState<number | null>(null);
  const [osList, setOsList] = useState<OS[]>([]);
  const [loadingOS, setLoadingOS] = useState(false);
  const [erroOS, setErroOS] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const porPagina = 100;
  const [modalOSAberto, setModalOSAberto] = useState(false);
  const [osIdSelecionada, setOsIdSelecionada] = useState<number | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState<{ id: number, descricao: string } | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  // Verificações de permissão
  const podeAcessar = temPermissao(permissoes, "os", "acessar_modulo");
  const podeCriar = temPermissao(permissoes, "os", "criar_os");
  const podeAlterar = temPermissao(permissoes, "os", "alterar_os");
  const podeExcluir = temPermissao(permissoes, "os", "excluir_os");
  const podeImprimir = temPermissao(permissoes, "os", "imprimir_os");
  const podeImprimirListagem = temPermissao(permissoes, "os", "imprimir_listagem");

  // Função para atualizar dados do usuário logado
  const atualizarDadosUsuario = useCallback(async () => {
    try {
      const response = await fetch(ENDPOINTS.USUARIO_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unidade: unidade.toLowerCase() })
      });
      const data = await response.json();

      if (Array.isArray(data)) {
        // Buscar o usuário logado na lista
        const usuarioAtual = data.find((u: { id: number }) => u.id === parseInt(loginLoginId));
        if (usuarioAtual) {
          console.log('Dados atualizados do usuário:', usuarioAtual);
          // Verificar se o departamento mudou
          const departamentoAtual = parseInt(loginDepartamentoId || "0");
          const novoDepartamento = usuarioAtual.departamento_id || 0;

          if (novoDepartamento !== departamentoAtual) {
            console.log(`Departamento atualizado: ${departamentoAtual} → ${novoDepartamento}`);
            // Retornar o novo departamento para ser atualizado no contexto
            return novoDepartamento;
          }
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
    }
    return null;
  }, [unidade, loginLoginId, loginDepartamentoId]);

  const fetchOS = useCallback((showLoading: boolean = true) => {
    if (showLoading) setLoadingOS(true);
    setErroOS(null);
    const payload: Record<string, string | number> = { unidade: unidade.toLowerCase() };
    // Se for executor (grupo_id = 2), inclui o usuario_os
    if (loginGrupoId === "2") {
      payload.usuario_os = parseInt(loginLoginId);
    }
    fetch(ENDPOINTS.OS_LIST, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setOsList(data.map(mapApiToOS));
        } else {
          setOsList([]);
        }
      })
      .catch(() => {
        setErroOS("Erro ao buscar as ordens de serviço.");
        setOsList([]);
      })
      .finally(() => {
        if (showLoading) setLoadingOS(false);
      });
  }, [unidade, loginGrupoId, loginLoginId]);

  useEffect(() => {
    fetchOS();
  }, [unidade, fetchOS]);

  useEffect(() => {
    const interval = setInterval(async () => {
      // Atualizar dados do usuário discretamente (sem loading)
      const novoDepartamento = await atualizarDadosUsuario();
      if (novoDepartamento !== null) {
        setLoginDepartamentoId(String(novoDepartamento));
      }
      // Atualizar OSs discretamente (sem loading)
      fetchOS(false);
    }, 10000);
    return () => clearInterval(interval);
  }, [unidade, fetchOS, atualizarDadosUsuario, setLoginDepartamentoId]);

  const filtrar = (os: OS & { executor_id?: number, departamento_id?: number }) => {
    // REGRA DE NEGÓCIO: Filtro por departamento baseado no grupo do usuário
    const isAdministrador = loginGrupoNome === "ADMINISTRADOR";
    const isGestor = loginGrupoNome === "OS GESTOR";
    const departamentoUsuario = parseInt(loginDepartamentoId || "0");
    const isExecutor = loginGrupoId === "4";

    // ADMINISTRADOR e OS GESTOR veem tudo
    if (isAdministrador || isGestor) {
      // Aplica apenas os filtros de interface
      return (
        (filtros.id === "" || os.id.toString().includes(filtros.id)) &&
        (filtros.inicio === undefined || filtros.inicio === "" || ((os.inicio || "").toLowerCase().includes(filtros.inicio.toLowerCase()))) &&
        (filtros.fim === undefined || filtros.fim === "" || ((os.fim || "").toLowerCase().includes(filtros.fim.toLowerCase()))) &&
        (filtros.departamento === "" || normalizar(os.departamento).includes(normalizar(filtros.departamento))) &&
        (filtros.local === "" || (os.local || "").toLowerCase().includes(filtros.local.toLowerCase())) &&
        (filtros.solicitante === "" || (os.solicitante || "").toLowerCase().includes(filtros.solicitante.toLowerCase())) &&
        (filtros.status === "" || (os.status || "").toLowerCase().includes(filtros.status.toLowerCase())) &&
        (filtros.executor === "" || (os.executor || "").toLowerCase().includes(filtros.executor.toLowerCase())) &&
        (filtros.prioridade === "" || (os.prioridade || "").toLowerCase().includes(filtros.prioridade.toLowerCase()))
      );
    }

    // REGRA PRINCIPAL: Se departamento_id > 0, filtro por departamento tem PRIORIDADE
    if (departamentoUsuario > 0) {
      const departamentoOS = (os as OS & { departamento_id?: number }).departamento_id || 0;
      console.log(`DEBUG: Usuario ${loginLoginNome} (dept: ${departamentoUsuario}) vs OS ${os.id} (dept: ${departamentoOS})`);
      if (departamentoOS !== departamentoUsuario) {
        return false; // Não pode ver esta OS
      }
    } else if (departamentoUsuario === 0 && isExecutor) {
      // Se departamento_id = 0 E for executor, aplica filtro de executor
      if (typeof os.executor_id !== 'undefined' && loginLoginId) {
        if (String(os.executor_id) !== String(loginLoginId)) {
          return false; // Não pode ver esta OS
        }
      } else {
        // Fallback: filtra por nome do executor
        if (os.executor?.toLowerCase() !== loginLoginNome?.toLowerCase()) {
          return false; // Não pode ver esta OS
        }
      }
    }
    // Se departamento_id = 0 E NÃO for executor, pode ver todas as OSs

    // Aplica os filtros de interface
    return (
      (filtros.id === "" || os.id.toString().includes(filtros.id)) &&
      (filtros.inicio === undefined || filtros.inicio === "" || ((os.inicio || "").toLowerCase().includes(filtros.inicio.toLowerCase()))) &&
      (filtros.fim === undefined || filtros.fim === "" || ((os.fim || "").toLowerCase().includes(filtros.fim.toLowerCase()))) &&
      (filtros.departamento === "" || normalizar(os.departamento).includes(normalizar(filtros.departamento))) &&
      (filtros.local === "" || (os.local || "").toLowerCase().includes(filtros.local.toLowerCase())) &&
      (filtros.solicitante === "" || (os.solicitante || "").toLowerCase().includes(filtros.solicitante.toLowerCase())) &&
      (filtros.status === "" || (os.status || "").toLowerCase().includes(filtros.status.toLowerCase())) &&
      (filtros.executor === "" || (os.executor || "").toLowerCase().includes(filtros.executor.toLowerCase())) &&
      (filtros.prioridade === "" || (os.prioridade || "").toLowerCase().includes(filtros.prioridade.toLowerCase()))
    );
  };

  const ordenar = (a: OS, b: OS) => {
    const { coluna, direcao } = sort;
    let valA = a[coluna];
    let valB = b[coluna];
    if (coluna === 'id') {
      return direcao === 'asc' ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
      if (valA < valB) return direcao === 'asc' ? -1 : 1;
      if (valA > valB) return direcao === 'asc' ? 1 : -1;
      return 0;
    }
  };

  const handleSort = (coluna: ColunaOrdenavel) => {
    setSort(s => ({
      coluna,
      direcao: s.coluna === coluna ? (s.direcao === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  // Paginação
  const osFiltradas = osList.filter(filtrar).sort(ordenar);
  const totalPaginas = Math.max(1, Math.ceil(osFiltradas.length / porPagina));
  const inicio = (pagina - 1) * porPagina;
  const fim = inicio + porPagina;
  const osPagina = osFiltradas.slice(inicio, fim);

  useEffect(() => {
    // Sempre que filtrar ou mudar a lista, volta para página 1
    setPagina(1);
  }, [unidade, filtros]);

  // Atualizar o filtro de status sempre que a prop mudar
  useEffect(() => {
    setFiltros(f => { const novo = { ...f, status: filtroStatus || "" }; if (onFiltrosChange) onFiltrosChange(novo); return novo; });
  }, [filtroStatus]);

  // Aplicar filtroDepartamentoInicial se vier como prop e mudar
  useEffect(() => {
    setFiltros(f => {
      const novo = { ...f, departamento: filtroDepartamentoInicial || "" };
      if (onFiltrosChange) onFiltrosChange(novo);
      return novo;
    });
    // eslint-disable-next-line
  }, [filtroDepartamentoInicial]);

  useEffect(() => {
    const beforePrint = () => setIsPrinting(true);
    const afterPrint = () => setIsPrinting(false);
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  // Função para gerar PDF
  function handleGerarPDF() {
    setGerandoPDF(true);
    setTimeout(() => { // Garante renderização do Loader
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const dataAtual = new Date();
      const dataStr = dataAtual.toLocaleDateString('pt-BR');
      const horaStr = dataAtual.toLocaleTimeString('pt-BR');
      const titulo = `ADVSERV - ${(unidade || '').toUpperCase()}`;
      doc.setFontSize(14);
      doc.text(titulo, 15, 15);
      doc.setFontSize(12);
      doc.text('LISTAGEM DE ORDENS DE SERVIÇO', 15, 22);
      doc.setFontSize(10);
      doc.text(`${dataStr}  ${horaStr}`, 285, 15, { align: 'right' });
      autoTable(doc, {
        startY: 28,
        head: [[
          'O.S', 'DEPARTAMENTO', 'LOCAL', 'SOLICITANTE', 'EXECUTOR', 'STATUS', 'PRIORIDADE', 'DESCRIÇÃO'
        ]],
        body: osFiltradas.map(os => [
          os.id,
          os.departamento,
          os.local,
          os.solicitante,
          os.executor,
          os.status,
          os.prioridade,
          os.descricao
        ]),
        styles: { fontSize: 10, cellPadding: 2, valign: 'middle' },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 16 }, // O.S
          1: { cellWidth: 36 }, // Departamento
          2: { cellWidth: 28 }, // Local
          3: { cellWidth: 28 }, // Solicitante
          4: { cellWidth: 36 }, // Executor
          5: { cellWidth: 26 }, // Status
          6: { cellWidth: 26 }, // Prioridade
          7: { cellWidth: 80 }, // Descrição
        },
        margin: { left: 12, right: 12 },
        pageBreak: 'auto',
        didDrawPage: () => {
          // Removido: numeração de páginas
        },
      });
      doc.save(`Relatorio_OS_${dataStr.replace(/\//g, '-')}_${horaStr.replace(/:/g, '-')}.pdf`);
      setGerandoPDF(false);
    }, 100);
  }

  async function handleImprimirOS(osId: number) {
    const response = await fetch(ENDPOINTS.OS_READ, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ unidade: unidade.toLowerCase(), id: osId })
    });
    const osData = await response.json();
    if (!osData || !osData[0]) {
      alert('Não foi possível buscar os dados da OS.');
      return;
    }
    const os = osData[0];
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    // Margens e medidas
    const left = 12, right = 198, top = 15;
    // Quadro superior: layout profissional em duas colunas
    const quadroTopY = top;
    const quadroTopH = 56;
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    doc.rect(left, quadroTopY, right - left, quadroTopH);
    // OS (grande)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.text('OS:', left + 4, quadroTopY + 11);
    doc.setFont('helvetica', 'bold');
    doc.text(String(os.id), left + 22, quadroTopY + 11);
    // Status (menor, mais à esquerda)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('STATUS:', left + 80, quadroTopY + 11);
    doc.setFont('helvetica', 'bold');
    doc.text(os.status_descricao || '', left + 120, quadroTopY + 11);
    // Prioridade
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.text('Prioridade:', left + 4, quadroTopY + 20);
    doc.setFont('helvetica', 'bold');
    doc.text(os.prioridade_descricao || '', left + 32, quadroTopY + 20);
    // Executor (menor, mais à esquerda, nome abaixo)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Executor:', left + 80, quadroTopY + 20);
    doc.setFont('helvetica', 'bold');
    doc.text(os.executor_nome || '', left + 80, quadroTopY + 26);
    // Solicitante (alinhado igual ao executor, na linha de 'Distribuída:')
    doc.setFont('helvetica', 'normal');
    doc.text('Solicitante:', left + 80, quadroTopY + 34);
    doc.setFont('helvetica', 'bold');
    doc.text(os.solicitante_nome || '', left + 80, quadroTopY + 40);
    // Datas (linhas 3-6)
    doc.setFontSize(12);
    let dataY = quadroTopY + 34;
    const espacamento = 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Abertura:', left + 4, dataY);
    doc.setFont('helvetica', 'bold');
    doc.text(os.dth_abertura || '', left + 32, dataY);
    dataY += espacamento;
    doc.setFont('helvetica', 'normal');
    doc.text('Distribuída:', left + 4, dataY);
    doc.setFont('helvetica', 'bold');
    doc.text(os.dth_distribuido || '', left + 32, dataY);
    dataY += espacamento;
    doc.setFont('helvetica', 'normal');
    doc.text('Aceita/Início:', left + 4, dataY);
    doc.setFont('helvetica', 'bold');
    doc.text(os.dth_inicio || '', left + 32, dataY);
    dataY += espacamento;
    doc.setFont('helvetica', 'normal');
    doc.text('Finalização:', left + 4, dataY);
    doc.setFont('helvetica', 'bold');
    doc.text(os.dth_fim || '', left + 32, dataY);
    // Nome do solicitante alinhado à direita
    // Ajustar mainY para o novo quadro superior
    const mainY = quadroTopY + quadroTopH + 6;
    // Departamento e Sala
    doc.setFontSize(11);
    doc.rect(left, mainY, 100, 8);
    doc.setFont('helvetica', 'normal');
    doc.text('Departamento', left + 2, mainY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(os.departamento_descricao || '', left + 28, mainY + 6);
    doc.rect(left + 100, mainY, 86, 8);
    doc.setFont('helvetica', 'normal');
    doc.text('Sala / Ambiente', right - 84, mainY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(os.local_descricao || '', right - 2, mainY + 6, { align: 'right' });
    // Descrição da Ocorrência
    doc.rect(left, mainY + 8, right - left, 16);
    doc.text('Descrição da Ocorrência', left + 2, mainY + 13);
    doc.setFontSize(10);
    doc.text(os.descricao_os || '', left + 2, mainY + 18, { maxWidth: right - left - 4 });
    // Observação do Validador e Observações do Administrador lado a lado
    doc.rect(left, mainY + 26, right - left, 24);
    doc.setFont('helvetica', 'normal');
    // Label do Validador à esquerda
    doc.text('Observação do Validador', left + 2, mainY + 31);
    // Label do Administrador à direita
    doc.text('Observações do Administrador', right - 2, mainY + 31, { align: 'right' });
    // Conteúdo do Validador abaixo do label, à esquerda (em negrito)
    doc.setFont('helvetica', 'bold');
    doc.text(os.observacoes || '', left + 2, mainY + 36, { maxWidth: (right - left) / 2 - 4 });
    // Conteúdo do Administrador abaixo do label, à direita (em negrito)
    doc.text(os.obs_adm || '', right - 2, mainY + 36, { align: 'right', maxWidth: (right - left) / 2 - 4 });
    // Título serviço executado
    doc.setFontSize(11);
    doc.rect(left, mainY + 42, right - left, 8);
    doc.text('DADOS REFERENTES AO SERVIÇO EXECUTADO', left + 2, mainY + 48);
    // Descrição do serviço realizado
    doc.rect(left, mainY + 50, right - left, 16);
    doc.setFont('helvetica', 'bold');
    doc.text('Descrição do Serviço a ser Realizado', left + 2, mainY + 55);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(os.descricao_servico_executar || '', left + 2, mainY + 60, { maxWidth: right - left - 4 });
    // Materiais e Equipamentos
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.rect(left, mainY + 66, 93, 24);
    doc.text('Materiais necessários para Execução', left + 2, mainY + 71);
    doc.rect(left + 93, mainY + 66, 93, 24);
    doc.text('Equipamentos Necessários', left + 95, mainY + 71);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(os.materiais_necessarios || '', left + 2, mainY + 76, { maxWidth: 89 });
    doc.text(os.equipamentos_necessarios || '', left + 95, mainY + 76, { maxWidth: 89 });
    // Profissionais envolvidos
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.rect(left, mainY + 90, right - left, 24);
    doc.text('Profissionais Envolvidos', left + 2, mainY + 95);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(os.profissionais_envolvidos || '', left + 2, mainY + 100, { maxWidth: right - left - 4 });
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  }

  // Função para excluir OS
  async function handleExcluirOS(osId: number) {
    setExcluindo(true);
    try {
      const response = await fetch(ENDPOINTS.OS_DELETE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unidade: unidade.toLowerCase(), id: osId })
      });
      const data = await response.json();
      if (data && data[0] && data[0].success) {
        setConfirmarExclusao(null);
        fetchOS(false);
      } else {
        alert('Não foi possível excluir a OS.');
      }
    } catch {
      alert('Erro ao excluir a OS.');
    }
    setExcluindo(false);
  }

  // Função para verificar se há algum filtro ativo
  const algumFiltroAtivo = Object.values(filtros).some(v => v && v !== "");

  // Função para limpar todos os filtros
  function handleLimparFiltros() {
    setFiltros({
      id: "",
      inicio: "",
      fim: "",
      departamento: "",
      local: "",
      solicitante: "",
      status: "",
      executor: "",
      prioridade: "",
    });
    if (onLimparFiltroStatus) onLimparFiltroStatus();
  }

  // Função para contar status nas OS filtradas
  const getContadoresFiltrados = () => {
    return {
      aguardando: osFiltradas.filter(os => os.status === 'AGUARDANDO').length,
      distribuidas: osFiltradas.filter(os => os.status === 'DISTRIBUIDA').length,
      aceita: osFiltradas.filter(os => os.status === 'ACEITA').length,
      concluidas: osFiltradas.filter(os => os.status === 'CONCLUIDA').length,
      canceladas: osFiltradas.filter(os => os.status === 'CANCELADA').length,
    };
  };

  // Expor a função para o componente pai via ref
  useImperativeHandle(ref, () => ({
    gerarPDF: handleGerarPDF,
    getContadoresFiltrados,
    filtros,
    fetchOS,
  }));

  // Chamar onFiltrosChange imediatamente na montagem, se houver filtro ativo
  useEffect(() => {
    if (onFiltrosChange) {
      const contadores = getContadoresFiltrados();
      onFiltrosChange({ ...filtros, contadores });
    }
  }, [filtros, osList, onFiltrosChange, getContadoresFiltrados]);

  if (!podeAcessar) {
    return <div className="p-8 text-center text-red-600 font-bold text-xl">Você não tem permissão para acessar este módulo.</div>;
  }

  return (
    <div className="w-full">
      {gerandoPDF && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <Loader />
        </div>
      )}
      <div className="overflow-x-auto rounded-lg shadow bg-white relative" style={{ maxWidth: '100vw' }}>
        {loadingOS && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <Loader />
          </div>
        )}
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12"></th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 w-20 text-center cursor-pointer select-none text-black" onClick={() => handleSort('id')}>
                  ID
                  {sort.coluna === 'id' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('inicio')}>
                  Início
                  {sort.coluna === 'inicio' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('fim')}>
                  Fim
                  {sort.coluna === 'fim' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('departamento')}>
                  Departamento
                  {sort.coluna === 'departamento' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('local')}>
                  Local
                  {sort.coluna === 'local' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('solicitante')}>
                  Solicitante
                  {sort.coluna === 'solicitante' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('status')}>
                  Status
                  {sort.coluna === 'status' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('executor')}>
                  Executor
                  {sort.coluna === 'executor' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 cursor-pointer select-none text-black" onClick={() => handleSort('prioridade')}>
                  Prioridade
                  {sort.coluna === 'prioridade' && (
                    <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="sticky top-0 z-20 bg-gray-100 p-2 h-12 text-center text-black">Ações</th>
              </tr>
              <tr className="bg-gray-50">
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10"></th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.id} onChange={v => setFiltros(f => ({ ...f, id: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.inicio || ""} onChange={v => setFiltros(f => ({ ...f, inicio: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.fim || ""} onChange={v => setFiltros(f => ({ ...f, fim: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.departamento} onChange={v => setFiltros(f => ({ ...f, departamento: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.local} onChange={v => setFiltros(f => ({ ...f, local: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.solicitante} onChange={v => setFiltros(f => ({ ...f, solicitante: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.status} onChange={v => setFiltros(f => ({ ...f, status: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.executor} onChange={v => setFiltros(f => ({ ...f, executor: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10">
                  <FiltroInput value={filtros.prioridade} onChange={v => setFiltros(f => ({ ...f, prioridade: v }))} placeholder="Filtrar..." />
                </th>
                <th className="sticky top-[48px] z-10 bg-gray-50 p-1 h-10"></th>
              </tr>
            </thead>
            <tbody>
              {loadingOS ? (
                // Skeleton loader para tabela de OS (mais linhas)
                Array.from({ length: 18 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    {Array.from({ length: 11 }).map((_, colIdx) => (
                      <td key={colIdx} className="p-2">
                        <div className="h-4 w-full bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : erroOS ? (
                <tr><td colSpan={12} className="text-center py-8 text-red-600">{erroOS}</td></tr>
              ) : osFiltradas.length === 0 ? (
                <tr><td colSpan={12} className="text-center text-gray-400 py-8">Nenhuma OS encontrada.</td></tr>
              ) : (
                (isPrinting ? osFiltradas : osPagina).map((os, idx) => (
                  <React.Fragment key={os.id}>
                    <tr
                      className={`cursor-pointer ${modalOSAberto && osIdSelecionada === os.id ? 'bg-teal-200' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-teal-200 ${(os.status === "CANCELADA") ? "line-through opacity-60" : ""} ${(idx > 0 && idx % 40 === 0) ? ' print-page-break' : ''}`}
                      onDoubleClick={() => {
                        setOsIdSelecionada(os.id);
                        setModalOSAberto(true);
                      }}
                      onMouseDown={event => { if (event.detail === 2 && event.button === 0) event.preventDefault(); }}
                    >
                      <td className="p-2 text-center text-black">
                        <button
                          className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 hover:bg-gray-300 border border-gray-300 text-lg font-bold cursor-pointer"
                          onClick={() => setExpandida(expandida === os.id ? null : os.id)}
                          title={expandida === os.id ? 'Fechar detalhes' : 'Ver detalhes'}
                        >
                          {expandida === os.id ? <span>-</span> : <span>+</span>}
                        </button>
                      </td>
                      <td className="p-2 font-semibold w-20 text-center text-black">{os.id}</td>
                      <td className="p-2 text-black">{os.inicio}</td>
                      <td className="p-2 text-black">{os.fim}</td>
                      <td className="p-2 text-black">{os.departamento}</td>
                      <td className="p-2 text-black">{os.local}</td>
                      <td className="p-2 text-black">{os.solicitante}</td>
                      <td className={`p-2 text-center font-bold rounded ${statusCores[os.status]}`}>{os.status}</td>
                      <td className="p-2 text-black">{os.executor}</td>
                      <td className={`p-2 text-center font-bold rounded ${prioridades[os.prioridade]}`}>{os.prioridade}</td>
                      <td className="p-2 flex gap-2 justify-center">
                        {podeAlterar && (
                          <button
                            className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 cursor-pointer"
                            title="Editar"
                            onClick={() => {
                              setOsIdSelecionada(os.id);
                              setModalOSAberto(true);
                            }}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                        )}
                        {podeExcluir && (
                          <button className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 cursor-pointer" title="Apagar" onClick={() => setConfirmarExclusao({ id: os.id, descricao: os.descricao })}><i className="fa fa-trash"></i></button>
                        )}
                        {podeImprimir && (
                          <button className="bg-gray-700 text-white px-2 py-1 rounded hover:bg-gray-800 cursor-pointer" title="Imprimir" onClick={() => handleImprimirOS(os.id)}><i className="fa fa-print"></i></button>
                        )}
                      </td>
                    </tr>
                    {expandida === os.id && (
                      <tr>
                        <td colSpan={12} className="bg-yellow-100 px-6 py-4 text-gray-800 text-base border-t border-yellow-200 animate-fade-in">
                          <b>Descrição da OS:</b><br />
                          {os.descricao}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Paginação + Atualizar juntos */}
      <div className="flex justify-center items-center gap-2 mt-6">
        <button className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold cursor-pointer" onClick={() => setPagina(1)} disabled={pagina === 1}>{"<<"}</button>
        <button className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold cursor-pointer" onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}>{"<"}</button>
        <span className="mx-2 text-black">Página <b>{pagina}</b> de <b>{totalPaginas}</b></span>
        <button className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold cursor-pointer" onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas}>{">"}</button>
        <button className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold cursor-pointer" onClick={() => setPagina(totalPaginas)} disabled={pagina === totalPaginas}>{">>"}</button>
        {/* Botão Limpar Filtros */}
        {algumFiltroAtivo && (
          <button
            className="ml-4 px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center gap-2 transition-colors duration-150"
            style={{ cursor: 'pointer' }}
            title="Limpar todos os filtros"
            onClick={handleLimparFiltros}
          >
            <i className="fa fa-eraser"></i>
            Limpar Filtros
          </button>
        )}
        <button
          className="ml-4 px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center gap-2"
          title="Atualizar agora"
          onClick={async () => {
            // Primeiro atualizar dados do usuário
            const novoDepartamento = await atualizarDadosUsuario();
            if (novoDepartamento !== null) {
              setLoginDepartamentoId(String(novoDepartamento));
            }
            // Depois atualizar as OSs
            fetchOS(true);
          }}
          disabled={loadingOS}
        >
          <i className="fa fa-rotate"></i>
          ATUALIZAR
        </button>
      </div>
      {modalOSAberto && osIdSelecionada && (
        <OSModal
          onClose={() => {
            setModalOSAberto(false);
            setOsIdSelecionada(null);
          }}
          unidade={unidade.toLowerCase()}
          osId={osIdSelecionada}
          onDistribuida={() => {
            setFiltros(f => ({ ...f, status: "" }));
            fetchOS(true);
          }}
        />
      )}
      {/* Diálogo de confirmação de exclusão */}
      {confirmarExclusao && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <style>{`
            .modal-anim {
              animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            }
            @keyframes zoomInFade {
              0% {
                opacity: 0;
                transform: scale(0.7);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
          <div className="modal-anim bg-white rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
            <div className="flex flex-col items-center mb-4">
              <span className="text-red-500 text-5xl mb-2"><i className="fa fa-times-circle"></i></span>
              <span className="text-xl font-bold mb-2 text-red-600">Confirma exclusão?</span>
              <span className="text-gray-700 mb-2">Deseja realmente excluir a OS <b>{confirmarExclusao.id}</b>?</span>
              <span className="text-gray-500 text-sm">{confirmarExclusao.descricao}</span>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              <button className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-bold" onClick={() => handleExcluirOS(confirmarExclusao.id)} disabled={excluindo}>
                {excluindo ? 'Excluindo...' : 'Excluir'}
              </button>
              <button className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 font-bold" onClick={() => setConfirmarExclusao(null)} disabled={excluindo}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Botão para criar nova OS */}
      {podeCriar && (
        <button
          onClick={() => setModalOSAberto(true)}
          className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-700 mb-4"
        >
          Nova OS
        </button>
      )}
      {/* Botão para imprimir listagem */}
      {podeImprimirListagem && (
        <button
          onClick={handleGerarPDF}
          className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700 mb-4 ml-2"
        >
          Imprimir Listagem
        </button>
      )}
    </div>
  );
});

export default OSGrade; 